import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import SignupLocationFields from "@/components/SignupLocationSelect";
import { GoldButton } from "@/components/Cards";
import { trackEvent } from "@/lib/firebase";

function Shell({ title, subtitle, children, wide }) {
  return (
    <div className="min-h-screen bg-dir-bg pt-[72px] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 section-noise opacity-40" />
      <div
        className={`relative w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-2xl glass p-8 sm:p-10`}
      >
        <Link to="/" className="mb-6 flex items-center gap-2.5">
          <img
            src="/assets/dirnation-logo.png"
            alt="DIR Nation — One People, Shared Future"
            className="h-auto w-40 object-contain"
          />
        </Link>
        <h1 className="font-display text-2xl font-bold text-dir-text">
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-sm text-dir-muted">{subtitle}</p>}
        <div className="mt-7">{children}</div>
      </div>
    </div>
  );
}

export function Input({ label, testId, ...props }) {
  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-sm text-dir-muted">{label}</label>
      )}
      <input
        data-testid={testId}
        {...props}
        className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 px-4 py-3 text-sm text-dir-text placeholder:text-dir-faint focus:outline-none focus:border-dir-gold/60"
      />
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      trackEvent("login_completed", { account_role: u.role });
      navigate(
        ["super_admin", "admin", "editor", "author", "moderator"].includes(
          u.role,
        )
          ? "/admin"
          : "/dashboard",
      );
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  return (
    <Shell title="Welcome back" subtitle="Log in to your DIR Nation account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Input
          label="Email"
          type="email"
          testId="login-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          label="Password"
          type="password"
          testId="login-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {err && (
          <p className="text-sm text-red-400" data-testid="login-error">
            {err}
          </p>
        )}
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-dir-muted">
            <input type="checkbox" className="accent-dir-gold" />
            Remember me
          </label>
          <Link to="/forgot-password" className="text-dir-gold">
            Forgot password?
          </Link>
        </div>
        <GoldButton
          className="w-full"
          data-testid="login-submit"
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Login"}
        </GoldButton>
      </form>
      <p className="mt-6 text-center text-sm text-dir-muted">
        No account?{" "}
        <Link to="/signup" className="text-dir-gold" data-testid="to-signup">
          Create Account
        </Link>
      </p>
    </Shell>
  );
}

export function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm: "",
    country: "",
    country_code: "",
    region: "",
    city: "",
    location_source: "",
  });
  const [terms, setTerms] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const signupStarted = useRef(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password !== f.confirm) {
      setErr("Passwords do not match");
      return;
    }
    if (!terms) {
      setErr("Please accept the terms");
      return;
    }
    setLoading(true);
    try {
      const { confirm, ...payload } = f;
      await register(payload);
      trackEvent("signup_completed", { location_selected: Boolean(f.country) });
      toast.success("Welcome to DIR Nation! Check your email to verify.");
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  return (
    <Shell
      wide
      title="Join DIR Nation"
      subtitle="Connect across the Horn and the global diaspora"
    >
      <form
        onSubmit={submit}
        onFocus={() => {
          if (!signupStarted.current) {
            signupStarted.current = true;
            trackEvent("signup_started");
          }
        }}
        className="space-y-4"
        data-testid="signup-form"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First Name"
            testId="signup-first"
            value={f.first_name}
            onChange={set("first_name")}
            required
          />
          <Input
            label="Last Name"
            testId="signup-last"
            value={f.last_name}
            onChange={set("last_name")}
            required
          />
        </div>
        <Input
          label="Email"
          type="email"
          testId="signup-email"
          value={f.email}
          onChange={set("email")}
          required
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Password"
            type="password"
            testId="signup-password"
            value={f.password}
            onChange={set("password")}
            required
          />
          <Input
            label="Confirm Password"
            type="password"
            testId="signup-confirm"
            value={f.confirm}
            onChange={set("confirm")}
            required
          />
        </div>
        <SignupLocationFields value={f} onChange={setF} />
        <label className="flex min-h-11 items-start gap-3 text-sm leading-6 text-dir-muted">
          <input
            data-testid="signup-terms"
            type="checkbox"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            className="mt-1 h-4 w-4 accent-dir-gold"
          />
          <span>
            I accept the{" "}
            <Link to="/terms" className="text-dir-gold hover:underline">
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-dir-gold hover:underline">
              Privacy Policy
            </Link>
            .
          </span>
        </label>
        {err && (
          <p className="text-sm text-red-400" data-testid="signup-error">
            {err}
          </p>
        )}
        <GoldButton
          className="w-full"
          data-testid="signup-submit"
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Create Account"
          )}
        </GoldButton>
      </form>
      <p className="mt-6 text-center text-sm text-dir-muted">
        Already a member?{" "}
        <Link to="/login" className="text-dir-gold" data-testid="to-login">
          Login
        </Link>
      </p>
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setMsg("Missing verification token");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then(() => setState("success"))
      .catch((e) => {
        setState("error");
        setMsg(formatApiErrorDetail(e.response?.data?.detail));
      });
  }, [params]);

  return (
    <Shell title="Email Verification">
      <div className="text-center" data-testid="verify-status">
        {state === "loading" && (
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-dir-gold" />
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-green-400" />
            <p className="mt-4 text-dir-text">
              Your email has been verified successfully!
            </p>
            <Link to="/dashboard">
              <GoldButton className="mt-6">Go to Dashboard</GoldButton>
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-400" />
            <p className="mt-4 text-dir-muted">
              {msg || "Verification failed."}
            </p>
            <Link to="/dashboard">
              <GoldButton className="mt-6">Back to Dashboard</GoldButton>
            </Link>
          </>
        )}
      </div>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true);
    }
  };
  return (
    <Shell
      title="Reset Password"
      subtitle="We'll send you a reset link if the email exists"
    >
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <Mail className="mx-auto h-12 w-12 text-dir-gold" />
          <p className="mt-4 text-dir-muted">
            If that email exists, a reset link has been sent.
          </p>
          <Link to="/login" className="mt-6 inline-block text-dir-gold">
            Back to Login
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Input
            label="Email"
            type="email"
            testId="forgot-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <GoldButton className="w-full" data-testid="forgot-submit">
            Send Reset Link
          </GoldButton>
          <p className="text-center text-sm text-dir-muted">
            <Link to="/login" className="text-dir-gold">
              Back to Login
            </Link>
          </p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/auth/reset-password", {
        token: params.get("token"),
        password,
      });
      toast.success("Password reset! Please log in.");
      navigate("/login");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Shell title="Set New Password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <Input
          label="New Password"
          type="password"
          testId="reset-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {err && <p className="text-sm text-red-400">{err}</p>}
        <GoldButton className="w-full" data-testid="reset-submit">
          Reset Password
        </GoldButton>
      </form>
    </Shell>
  );
}
